import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import { getBethHabadLabel, walkMinutesFrom } from "../utils";
import StarRating from "../components/StarRating";

const detailStyles = `
/* Hero image */
.tr-hdetail__hero {
  max-width: 1180px;
  margin: 24px auto 32px;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}
.tr-hdetail__hero-img {
  width: 100%;
  aspect-ratio: 16/7;
  object-fit: cover;
  border-radius: 32px;
  display: block;
  background: #e5e7eb;
}
.tr-hdetail__hero-placeholder {
  width: 100%;
  aspect-ratio: 16/7;
  border-radius: 32px;
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
}

/* Container */
.tr-hdetail__body {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
  width: 100%;
}

/* Title */
.tr-hdetail__title {
  font-size: clamp(26px, 4vw, 40px);
  font-weight: 800;
  color: #1a1a2e;
  margin: 0 0 20px;
  line-height: 1.2;
}

/* Grid 2 colonnes */
.tr-hdetail__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 380px);
  gap: 40px;
  align-items: start;
}
.tr-hdetail__grid > * { min-width: 0; }
@media (max-width: 900px) {
  .tr-hdetail__grid {
    grid-template-columns: 1fr;
  }
}

/* ── Colonne gauche ── */
.tr-hdetail__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 18px;
}
.tr-hdetail__tag {
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
  padding: 6px 14px;
  border-radius: 999px;
  background: #ffe8d9;
  color: #1a1a2e;
}
.tr-hdetail__tag--kosher {
  background: #dcfce7;
  color: #166534;
}

/* Distance */
.tr-hdetail__distance {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 18px;
  font-size: 14px;
  color: #374151;
}
.tr-hdetail__distance b {
  color: #1a1a2e;
  font-weight: 700;
}
.tr-hdetail__distance-icon {
  color: var(--primary, #1b579e);
  display: flex;
}

/* Boutons ronds action */
.tr-hdetail__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 24px;
}
.tr-hdetail__action {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-decoration: none;
  color: #1a1a2e;
  font-size: 12px;
  font-weight: 500;
}
.tr-hdetail__action-btn {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary, #1b579e);
  transition: transform .15s, box-shadow .15s;
}
.tr-hdetail__action:hover .tr-hdetail__action-btn {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px color-mix(in srgb, var(--primary, #1b579e) 25%, transparent);
}

/* Rich description */
.tr-hdetail__description {
  margin-top: 18px;
  font-size: 15px;
  line-height: 1.7;
  color: #1f2937;
  max-width: 720px;
  overflow-wrap: anywhere;
}
.tr-hdetail__description img { max-width: 100%; height: auto; }
.tr-hdetail__description h1,
.tr-hdetail__description h2,
.tr-hdetail__description h3 { color: #1a1a2e; margin-top: 24px; margin-bottom: 10px; }
.tr-hdetail__description p { margin: 0 0 12px; }
.tr-hdetail__description a { color: var(--primary, #1b579e); text-decoration: underline; }

/* Galerie desktop */
.tr-hdetail__gallery {
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
  max-width: 720px;
}
.tr-hdetail__gallery-img {
  width: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  border-radius: 16px;
  display: block;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,.08);
  transition: transform .2s ease;
}
.tr-hdetail__gallery-img:hover { transform: scale(1.02); }

/* Galerie mobile : pile 3D */
.tr-hdetail__gallery-stack {
  margin-top: 32px;
  display: none;
  cursor: pointer;
  padding: 8px 24px 8px 8px;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}
.tr-hdetail__gallery-stack-inner {
  position: relative;
  width: 100%;
  max-width: 340px;
  aspect-ratio: 4/3;
  margin: 0 auto;
}
.tr-hdetail__gallery-stack-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 18px;
  box-shadow: 0 10px 30px rgba(0,0,0,.18), 0 3px 8px rgba(0,0,0,.08);
  border: 3px solid white;
  transition: transform .35s ease;
}
.tr-hdetail__gallery-stack-img--back-2 {
  transform: rotate(-6deg) translate(-12px, 10px) scale(.94);
  filter: brightness(.9);
  z-index: 1;
}
.tr-hdetail__gallery-stack-img--back-1 {
  transform: rotate(4deg) translate(10px, 6px) scale(.97);
  filter: brightness(.95);
  z-index: 2;
}
.tr-hdetail__gallery-stack-img--front {
  z-index: 3;
}
.tr-hdetail__gallery-stack:active .tr-hdetail__gallery-stack-img--back-2 {
  transform: rotate(-10deg) translate(-20px, 14px) scale(.9);
}
.tr-hdetail__gallery-stack:active .tr-hdetail__gallery-stack-img--back-1 {
  transform: rotate(8deg) translate(16px, 10px) scale(.93);
}
.tr-hdetail__gallery-stack:active .tr-hdetail__gallery-stack-img--front {
  transform: scale(1.02);
}
.tr-hdetail__gallery-stack-badge {
  position: absolute;
  bottom: 14px;
  right: 14px;
  background: rgba(0,0,0,.65);
  color: white;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  z-index: 4;
  backdrop-filter: blur(4px);
}
.tr-hdetail__gallery-stack-hint {
  margin-top: 18px;
  text-align: center;
  font-size: 14px;
  color: #4b5563;
  font-weight: 500;
}
.tr-hdetail__gallery-stack-hint span {
  color: var(--primary, #1b579e);
  font-weight: 700;
}

@media (max-width: 640px) {
  .tr-hdetail__gallery { display: none; }
  .tr-hdetail__gallery-stack { display: block; }
}

/* Lightbox */
.tr-hdetail__lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
  cursor: zoom-out;
}
.tr-hdetail__lightbox img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0,0,0,.5);
}
.tr-hdetail__lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: background .2s;
  z-index: 2;
}
.tr-hdetail__lightbox-close:hover { background: rgba(255,255,255,.25); }
.tr-hdetail__lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: rgba(255,255,255,.15);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  transition: background .2s, transform .2s;
  z-index: 2;
}
.tr-hdetail__lightbox-nav:hover { background: rgba(255,255,255,.28); }
.tr-hdetail__lightbox-nav--prev { left: 20px; }
.tr-hdetail__lightbox-nav--next { right: 20px; }
.tr-hdetail__lightbox-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(255,255,255,.12);
  color: white;
  font-size: 13px;
  font-weight: 500;
  z-index: 2;
}
@media (max-width: 640px) {
  .tr-hdetail__lightbox-nav { width: 44px; height: 44px; }
  .tr-hdetail__lightbox-nav--prev { left: 10px; }
  .tr-hdetail__lightbox-nav--next { right: 10px; }
}

/* ── Colonne droite : carte infos ── */
.tr-hdetail__side {
  background: white;
  border-radius: 24px;
  padding: 22px 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.04);
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: sticky;
  top: 88px;
  max-width: 100%;
  box-sizing: border-box;
}
.tr-hdetail__side-row {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
  color: #374151;
  font-size: 14px;
  line-height: 1.4;
  min-width: 0;
}
.tr-hdetail__side-row span:last-child { min-width: 0; overflow-wrap: anywhere; }
.tr-hdetail__side-row:hover { color: #1a1a2e; }
.tr-hdetail__side-icon {
  width: 40px; height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tr-hdetail__side-icon--location,
.tr-hdetail__side-icon--phone,
.tr-hdetail__side-icon--email,
.tr-hdetail__side-icon--whatsapp {
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  color: var(--primary, #1b579e);
}

/* Back link */
.tr-hdetail__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--primary, #1b579e);
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 18px;
  text-decoration: none;
}
.tr-hdetail__back:hover { text-decoration: underline; }
`;

function ActionButton({ type, href, label, icon, external }) {
    return (
        <a
            href={href}
            className={`tr-hdetail__action tr-hdetail__action--${type}`}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
        >
            <span className="tr-hdetail__action-btn">
                {renderIcon(icon, 26)}
            </span>
            <span>{label}</span>
        </a>
    );
}

export default function HotelDetail({ context, loading }) {
    const { org, config, travel } = context;
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    const placeId = context.route?.[2];
    const rawPlace = (travel?.hotelPlaces || []).find(p => p.id === placeId);

    const [lightboxIndex, setLightboxIndex] = React.useState(null);

    if (!rawPlace) {
        return (
            <div className="travel-page travel-page--hotels-detail">
                <Meta context={context} title={org.name} description={org.name} />
                {loading && <Loader />}
                <Header context={context} />
                <main className="travel-main">
                    <div className="travel-container" style={{ padding: "80px 24px", textAlign: "center" }}>
                        <h1><Trans>Hébergement introuvable</Trans></h1>
                        <Link href={org.BASE_WEB_URL + "/travel/hotels"} legacyBehavior>
                            <a className="tr-hdetail__back">
                                {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                                <Trans>Retour à la liste</Trans>
                            </a>
                        </Link>
                    </div>
                </main>
                <Footer context={context} />
            </div>
        );
    }

    const place = applyLang(rawPlace, context.lang);
    const categories = (travel?.hotel?.categories || []).map(c => applyLang(c, context.lang));
    const category = categories.find(c => c.id === place.categoryId) || null;

    const images = Array.isArray(place.images) ? place.images : [];
    const thumb = place.thumbnail || images[0] || null;
    const heroSrc = thumb ? `${ASSET_URL}${thumb}` : null;
    const galleryImages = images.filter(i => i !== thumb);

    const closeLightbox = () => setLightboxIndex(null);
    const showPrev = React.useCallback(() => {
        setLightboxIndex((i) => (i == null ? null : (i - 1 + galleryImages.length) % galleryImages.length));
    }, [galleryImages.length]);
    const showNext = React.useCallback(() => {
        setLightboxIndex((i) => (i == null ? null : (i + 1) % galleryImages.length));
    }, [galleryImages.length]);

    React.useEffect(() => {
        if (lightboxIndex == null) return;
        const onKey = (e) => {
            if (e.key === "Escape") closeLightbox();
            else if (e.key === "ArrowLeft") showPrev();
            else if (e.key === "ArrowRight") showNext();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [lightboxIndex, showPrev, showNext]);

    const address = place.address?.address;
    const lat = place.address?.lat;
    const lon = place.address?.lon;
    const navUrl = lat != null && lon != null
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
        : (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null);

    const walkMinutes = walkMinutesFrom(org, place);

    const contacts = place.contacts || [];
    const phoneContact = contacts.find(c => c.type === "phone");
    const emailContact = contacts.find(c => c.type === "email");
    const whatsappContact = contacts.find(c => c.type === "whatsapp");

    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const placeDesc = stripHtml(place.description || place.comment) || t`${place.name} — hébergement proche du ${bethHabadLabel} de ${org.ville || org.name}`;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "LodgingBusiness",
        name: place.name,
        description: placeDesc,
        image: heroSrc || undefined,
        address: address ? { "@type": "PostalAddress", streetAddress: address } : undefined,
        geo: (lat != null && lon != null) ? {
            "@type": "GeoCoordinates",
            latitude: lat,
            longitude: lon,
        } : undefined,
        telephone: phoneContact?.value,
        email: emailContact?.value,
        starRating: place.stars ? { "@type": "Rating", ratingValue: place.stars } : undefined,
    };

    return (
        <div className="travel-page travel-page--hotels-detail tr-hdetail">
            <style dangerouslySetInnerHTML={{ __html: detailStyles }} />
            <Meta
                context={context}
                title={place.name}
                description={placeDesc.slice(0, 200)}
                image={heroSrc}
                type="hotel"
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">

                {/* Hero image */}
                <div className="tr-hdetail__hero">
                    {heroSrc ? (
                        <img src={heroSrc} alt={place.name} className="tr-hdetail__hero-img" />
                    ) : (
                        <div className="tr-hdetail__hero-placeholder">
                            {renderIcon({ name: "IconBuildingSkyscraper", source: "tabler" }, 64)}
                        </div>
                    )}
                </div>

                <div className="tr-hdetail__body">

                    <Link href={org.BASE_WEB_URL + "/travel/hotels"} legacyBehavior>
                        <a className="tr-hdetail__back">
                            {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                            <Trans>Retour</Trans>
                        </a>
                    </Link>

                    <h1 className="tr-hdetail__title">{place.name}</h1>

                    {place.note > 0 && (
                        <div style={{ marginBottom: 18 }}>
                            <StarRating value={place.note} size={20} showValue />
                        </div>
                    )}

                    <div className="tr-hdetail__grid">

                        {/* Colonne gauche */}
                        <div>
                            <div className="tr-hdetail__tags">
                                {category?.label && (
                                    <span className="tr-hdetail__tag">{category.label}</span>
                                )}
                                {place.isKosher && (
                                    <span className="tr-hdetail__tag tr-hdetail__tag--kosher">
                                        <Trans>Casher</Trans>
                                    </span>
                                )}
                            </div>

                            {walkMinutes != null && (
                                <div className="tr-hdetail__distance">
                                    <span className="tr-hdetail__distance-icon">
                                        {renderIcon({ name: "IconWalk", source: "tabler" }, 18)}
                                    </span>
                                    <span>
                                        <b><Trans>Distance du {bethHabadLabel} :</Trans></b>{" "}
                                        <Trans>{walkMinutes} minutes à pied</Trans>
                                    </span>
                                </div>
                            )}

                            {/* Description HTML */}
                            {place.description && (
                                <div
                                    className="tr-hdetail__description"
                                    dangerouslySetInnerHTML={{ __html: place.description }}
                                />
                            )}

                            {/* Actions */}
                            <div className="tr-hdetail__actions">
                                {phoneContact && (
                                    <ActionButton
                                        type="phone"
                                        href={`tel:${phoneContact.value}`}
                                        label={t`Appel`}
                                        icon={{ name: "IconPhone", source: "tabler" }}
                                    />
                                )}
                                {navUrl && (
                                    <ActionButton
                                        type="nav"
                                        href={navUrl}
                                        label={t`Naviguer`}
                                        icon={{ name: "IconMapPin", source: "tabler" }}
                                        external
                                    />
                                )}
                                {whatsappContact && (
                                    <ActionButton
                                        type="whatsapp"
                                        href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                        label="WhatsApp"
                                        icon={{ name: "IconBrandWhatsapp", source: "tabler" }}
                                        external
                                    />
                                )}
                                {emailContact && (
                                    <ActionButton
                                        type="email"
                                        href={`mailto:${emailContact.value}`}
                                        label={t`E-mail`}
                                        icon={{ name: "IconMail", source: "tabler" }}
                                    />
                                )}
                                {place.bookingUrl && (
                                    <ActionButton
                                        type="booking"
                                        href={place.bookingUrl}
                                        label={t`Réserver`}
                                        icon={{ name: "IconCalendarCheck", source: "tabler" }}
                                        external
                                    />
                                )}
                            </div>

                            {/* Galerie desktop : grille */}
                            {galleryImages.length > 0 && (
                                <div className="tr-hdetail__gallery">
                                    {galleryImages.map((img, i) => (
                                        <img
                                            key={i}
                                            src={`${ASSET_URL}${img}`}
                                            alt={`${place.name} - ${i + 2}`}
                                            className="tr-hdetail__gallery-img"
                                            loading="lazy"
                                            onClick={() => setLightboxIndex(i)}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Galerie mobile : pile 3D cliquable */}
                            {galleryImages.length > 0 && (
                                <div
                                    className="tr-hdetail__gallery-stack"
                                    onClick={() => setLightboxIndex(0)}
                                    role="button"
                                    aria-label={t`Voir les photos`}
                                >
                                    <div className="tr-hdetail__gallery-stack-inner">
                                        {galleryImages[2] && (
                                            <img
                                                src={`${ASSET_URL}${galleryImages[2]}`}
                                                alt=""
                                                className="tr-hdetail__gallery-stack-img tr-hdetail__gallery-stack-img--back-2"
                                                loading="lazy"
                                            />
                                        )}
                                        {galleryImages[1] && (
                                            <img
                                                src={`${ASSET_URL}${galleryImages[1]}`}
                                                alt=""
                                                className="tr-hdetail__gallery-stack-img tr-hdetail__gallery-stack-img--back-1"
                                                loading="lazy"
                                            />
                                        )}
                                        <img
                                            src={`${ASSET_URL}${galleryImages[0]}`}
                                            alt={place.name}
                                            className="tr-hdetail__gallery-stack-img tr-hdetail__gallery-stack-img--front"
                                            loading="lazy"
                                        />
                                        <span className="tr-hdetail__gallery-stack-badge">
                                            {renderIcon({ name: "IconPhoto", source: "tabler" }, 14)}
                                            {galleryImages.length} <Trans>photos</Trans>
                                        </span>
                                    </div>
                                    <div className="tr-hdetail__gallery-stack-hint">
                                        <Trans>Touchez pour <span>voir toutes les photos</span></Trans>
                                    </div>
                                </div>
                            )}

                        </div>

                        {/* Colonne droite : carte latérale */}
                        <aside className="tr-hdetail__side">
                            {address && (
                                <a
                                    href={navUrl || "#"}
                                    className="tr-hdetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-hdetail__side-icon tr-hdetail__side-icon--location">
                                        {renderIcon({ name: "IconMapPin", source: "tabler" }, 20)}
                                    </span>
                                    <span>{address}</span>
                                </a>
                            )}
                            {phoneContact && (
                                <a href={`tel:${phoneContact.value}`} className="tr-hdetail__side-row">
                                    <span className="tr-hdetail__side-icon tr-hdetail__side-icon--phone">
                                        {renderIcon({ name: "IconPhone", source: "tabler" }, 20)}
                                    </span>
                                    <span>{phoneContact.value}</span>
                                </a>
                            )}
                            {whatsappContact && (
                                <a
                                    href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`}
                                    className="tr-hdetail__side-row"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <span className="tr-hdetail__side-icon tr-hdetail__side-icon--whatsapp">
                                        {renderIcon({ name: "IconBrandWhatsapp", source: "tabler" }, 20)}
                                    </span>
                                    <span>{whatsappContact.value}</span>
                                </a>
                            )}
                            {emailContact && (
                                <a href={`mailto:${emailContact.value}`} className="tr-hdetail__side-row">
                                    <span className="tr-hdetail__side-icon tr-hdetail__side-icon--email">
                                        {renderIcon({ name: "IconMail", source: "tabler" }, 20)}
                                    </span>
                                    <span>{emailContact.value}</span>
                                </a>
                            )}
                        </aside>

                    </div>

                </div>
            </main>

            {lightboxIndex != null && galleryImages[lightboxIndex] && (
                <div className="tr-hdetail__lightbox" onClick={closeLightbox}>
                    <button
                        className="tr-hdetail__lightbox-close"
                        onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
                        aria-label={t`Fermer`}
                    >
                        {renderIcon({ name: "IconX", source: "tabler" }, 22)}
                    </button>
                    {galleryImages.length > 1 && (
                        <>
                            <button
                                className="tr-hdetail__lightbox-nav tr-hdetail__lightbox-nav--prev"
                                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                                aria-label={t`Image précédente`}
                            >
                                {renderIcon({ name: "IconChevronLeft", source: "tabler" }, 28)}
                            </button>
                            <button
                                className="tr-hdetail__lightbox-nav tr-hdetail__lightbox-nav--next"
                                onClick={(e) => { e.stopPropagation(); showNext(); }}
                                aria-label={t`Image suivante`}
                            >
                                {renderIcon({ name: "IconChevronRight", source: "tabler" }, 28)}
                            </button>
                            <span className="tr-hdetail__lightbox-counter">
                                {lightboxIndex + 1} / {galleryImages.length}
                            </span>
                        </>
                    )}
                    <img
                        src={`${ASSET_URL}${galleryImages[lightboxIndex]}`}
                        alt=""
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            )}

            <Footer context={context} />
        </div>
    );
}
